/** Shared GapSessionHeader directive formatter, used by the backend finalize append and the frontend live preview. */

import { formatInTimeZone } from 'date-fns-tz'

/** The directive name, shared so the frontend descriptor and any parser reference one constant. */
export const GAP_SESSION_HEADER_DIRECTIVE = 'GapSessionHeader'

/** How a transcript block came about: recorded by a Recording Session, or added by the advisor. */
export type SessionSource = 'added'

/** One block's boundary as carried by a GapSessionHeader directive. */
export interface SessionHeader {
   startedAt: string
   /** Absent on a live session, and on headers written before durations were recorded. */
   endedAt?: string
   /** Absent on a Recording Session's own block, which is the default. */
   source?: SessionSource
}

const DIRECTIVE_PATTERN = new RegExp(`::${GAP_SESSION_HEADER_DIRECTIVE}\\{([^}]*)\\}`, 'g')

function readAttribute(params: { attributes: string; name: string }): string | undefined {
   const match = new RegExp(`${params.name}="([^"]*)"`).exec(params.attributes)
   return match?.[1] || undefined
}

/** Emits the GapSessionHeader leaf-directive markdown for one block. */
export function formatGapSessionHeader(params: { startedAt: Date; endedAt?: Date | null; source?: SessionSource }): string {
   const attributes = [`startedAt="${params.startedAt.toISOString()}"`]
   if (params.endedAt) attributes.push(`endedAt="${params.endedAt.toISOString()}"`)
   if (params.source) attributes.push(`source="${params.source}"`)
   return `::${GAP_SESSION_HEADER_DIRECTIVE}{${attributes.join(' ')}}`
}

/** Reads a header's source attribute, ignoring any value this version doesn't know. */
export function readSessionSource(attribute: string | undefined): SessionSource | undefined {
   return attribute === 'added' ? 'added' : undefined
}

/** The marker shown above transcript content that precedes the first recorded session. */
export const SESSION_PREAMBLE_LABEL = 'Earlier transcript'

/** The marker shown above a block the advisor added rather than recorded. */
export const SESSION_ADDED_LABEL = 'Added transcript'

/** One session's slice of a transcript markdown, its header directive included. */
export interface SessionBlock {
   /** Absent on content preceding the first header, and on a header missing its `startedAt`. */
   header?: SessionHeader
   /** True for the content preceding the first header, which no Recording Session produced. */
   isPreamble?: boolean
   markdown: string
}

/**
 * Splits a transcript markdown into its Recording Session blocks, in document order.
 * @param markdown The stitched transcript markdown.
 * @returns One block per GapSessionHeader, preceded by any headerless content.
 */
export function splitSessionBlocks(markdown: string): SessionBlock[] {
   const matches = [...markdown.matchAll(DIRECTIVE_PATTERN)]
   const blocks: SessionBlock[] = []

   const firstHeaderAt = matches.length > 0 ? matches[0].index ?? 0 : markdown.length
   const preamble = markdown.slice(0, firstHeaderAt).trim()
   if (preamble.length > 0) blocks.push({ isPreamble: true, markdown: preamble })

   matches.forEach((match, index) => {
      const nextMatch = matches[index + 1]
      const blockEnd = nextMatch ? nextMatch.index ?? markdown.length : markdown.length
      const startedAt = readAttribute({ attributes: match[1], name: 'startedAt' })
      blocks.push({
         header: startedAt ? {
            startedAt,
            endedAt: readAttribute({ attributes: match[1], name: 'endedAt' }),
            source: readSessionSource(readAttribute({ attributes: match[1], name: 'source' })),
         } : undefined,
         markdown: markdown.slice(match.index ?? 0, blockEnd).trim(),
      })
   })

   return blocks
}

/** Extracts every GapSessionHeader in a transcript markdown, in document order. */
export function parseSessionHeaders(markdown: string): SessionHeader[] {
   return splitSessionBlocks(markdown).flatMap(block => block.header ? [block.header] : [])
}

/** Extracts every GapSessionHeader's `startedAt`, in document order, for ordinal and count derivation. */
export function parseSessionHeaderStartedAts(markdown: string): string[] {
   return parseSessionHeaders(markdown).map(header => header.startedAt)
}

/** A transcript's session structure, as the editor and the PDF both need it to render their markers. */
export interface SessionLayout {
   /** Ordered `startedAt` of every recorded session header, so each derives its ordinal by index. */
   startedAts: string[]
   /** Whether content precedes the first header, e.g. a pre-cutover transcript. */
   hasPreamble: boolean
   /** How many blocks the transcript holds, the preamble and the added blocks counted. */
   blockCount: number
   /** Whether more than one block is present, so the markers are worth rendering. */
   hasMultipleSessions: boolean
}

/**
 * Reads a transcript's session structure for ordinal and marker rendering.
 * @param markdown The stitched transcript markdown.
 * @returns The ordered header starts, whether a preamble is present, and how many blocks there are.
 */
export function readSessionLayout(markdown: string): SessionLayout {
   const blocks = splitSessionBlocks(markdown)
   const hasPreamble = blocks.some(block => block.isPreamble)
   const headers = blocks.flatMap(block => block.header ? [block.header] : [])
   const startedAts = headers.filter(header => !header.source).map(header => header.startedAt)
   const blockCount = headers.length + (hasPreamble ? 1 : 0)
   return { startedAts, hasPreamble, blockCount, hasMultipleSessions: blockCount > 1 }
}

/**
 * Derives a recorded session's ordinal from its place among the layout's starts.
 * @param params The document's layout, and the header's start.
 * @returns The one-based ordinal, falling back to the last when the start isn't in the layout.
 */
export function readSessionOrdinal(params: { layout: SessionLayout; startedAt?: string }): number {
   const index = params.startedAt ? params.layout.startedAts.indexOf(params.startedAt) : -1
   return index >= 0 ? index + 1 : params.layout.startedAts.length
}

/**
 * Formats a session's recorded length as `1h 24m` / `43m` / `< 1m`.
 * @param params The session's start and end datetimes, in any parseable form.
 * @returns The formatted duration, or undefined when it can't be derived.
 */
export function formatSessionDuration(params: { startedAt: string | Date; endedAt: string | Date | null | undefined }): string | undefined {
   if (!params.endedAt) return undefined
   const startedAt = new Date(params.startedAt).getTime()
   const endedAt = new Date(params.endedAt).getTime()
   if (isNaN(startedAt) || isNaN(endedAt) || endedAt < startedAt) return undefined
   const totalMinutes = Math.floor((endedAt - startedAt) / 60000)
   if (totalMinutes < 1) return '< 1m'
   const hours = Math.floor(totalMinutes / 60)
   const minutes = totalMinutes % 60
   return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

/** The one label format both the PDF and the editor render, so a session reads the same on either. */
const SESSION_LABEL_FORMAT = 'EEE d MMMM, h:mm a'

/**
 * Formats one session's date, time and recorded length for display beside its ordinal.
 * @param params The session's boundary, and the IANA timezone to render it in.
 * @returns The formatted label, or undefined when the start can't be parsed.
 */
export function formatSessionHeaderLabel(params: { startedAt: string | Date; endedAt?: string | Date | null; timezone?: string }): string | undefined {
   const startedAt = new Date(params.startedAt)
   if (isNaN(startedAt.getTime())) return undefined
   const label = formatInTimeZone(startedAt, params.timezone?.trim() || 'UTC', SESSION_LABEL_FORMAT)
   const duration = formatSessionDuration({ startedAt, endedAt: params.endedAt })
   return duration ? `${label} · ${duration}` : label
}

/**
 * Formats one transcript block's marker label, as both the PDF and the editor render it.
 * @param params The block's header, its ordinal among the recorded sessions, and the viewer's timezone.
 * @returns The label, prefixed by the ordinal for a recorded session and by the added marker otherwise.
 */
export function formatSessionBlockLabel(params: { header: SessionHeader; ordinal: number; timezone?: string }): string {
   const label = formatSessionHeaderLabel({
      startedAt: params.header.startedAt,
      endedAt: params.header.endedAt,
      timezone: params.timezone,
   })
   const prefix = params.header.source === 'added' ? SESSION_ADDED_LABEL : `Session ${params.ordinal}`
   return label ? `${prefix} · ${label}` : prefix
}

/**
 * Splices one block into a transcript markdown, ordered by `startedAt` (ADR 0001).
 * @param params The existing markdown, the block's start and end, how it came about, and its rendered content.
 * @returns The composed markdown.
 */
export function insertSessionBlock(params: {
   existingMarkdown: string
   startedAt: Date
   endedAt?: Date | null
   source?: SessionSource
   sessionMarkdown: string
}): string {
   const header = formatGapSessionHeader({ startedAt: params.startedAt, endedAt: params.endedAt, source: params.source })
   const block = `${header}\n\n${params.sessionMarkdown}`
   const existingMarkdown = params.existingMarkdown.trim()
   if (existingMarkdown.length === 0) return block

   const startedAt = params.startedAt.getTime()
   const blocks = splitSessionBlocks(existingMarkdown)
   // Headerless content and unreadable starts never compare later, so they keep their place.
   const insertAt = blocks.findIndex(existingBlock => {
      const existingStartedAt = existingBlock.header ? new Date(existingBlock.header.startedAt).getTime() : NaN
      return !isNaN(existingStartedAt) && existingStartedAt > startedAt
   })
   if (insertAt < 0) return `${existingMarkdown}\n\n${block}`

   const markdowns = blocks.map(existingBlock => existingBlock.markdown)
   return [...markdowns.slice(0, insertAt), block, ...markdowns.slice(insertAt)].join('\n\n')
}
